import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { Save } from 'lucide-react';

const FeatureForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      category: 'Altro'
    }
  });

  useEffect(() => {
    if (isEditing) {
      fetchFeature();
    }
    fetchCategories();
  }, [isEditing, id]);

  const fetchFeature = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const { data: feature, error } = await supabase
        .from('features')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      if (feature) {
        // Set form values
        setValue('name', feature.name);
        setValue('category', feature.category || 'Altro');
      }
    } catch (error) {
      console.error('Error fetching feature:', error);
      toast.error('Errore nel caricamento della dotazione');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('features')
        .select('category')
        .not('category', 'is', null);
      
      if (error) throw error;
      
      if (data) {
        // Extract unique categories
        const uniqueCategories = [...new Set(data.map(item => item.category).filter(Boolean))];
        setCategories(['Altro', ...uniqueCategories]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const onSubmit = async (formData: any) => {
    setSubmitLoading(true);
    
    try {
      if (isEditing) {
        const { error } = await supabase
          .from('features')
          .update(formData)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Dotazione aggiornata con successo');
      } else {
        const { error } = await supabase
          .from('features')
          .insert(formData);
        
        if (error) throw error;
        
        toast.success('Dotazione creata con successo');
      }
      
      navigate('/admin/features');
    } catch (error: any) {
      console.error('Error saving feature:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante il salvataggio'}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? "Modifica Dotazione" : "Nuova Dotazione"}>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? "Modifica Dotazione" : "Nuova Dotazione"}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Informazioni Dotazione</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome*
              </label>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Il nome è obbligatorio' }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. Aria Condizionata"
                  />
                )}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <input
                      list="categories"
                      {...field}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                      placeholder="Es. Interni, Esterni, etc."
                    />
                    <datalist id="categories">
                      {categories.map((category, index) => (
                        <option key={index} value={category} />
                      ))}
                    </datalist>
                  </div>
                )}
              />
              <p className="text-xs text-gray-500 mt-1">Puoi selezionare una categoria esistente o crearne una nuova</p>
            </div>
          </div>
        </div>
        
        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/features')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitLoading}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitLoading ? (
              <>
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Salva Dotazione
              </>
            )}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default FeatureForm;